<script setup>
import Countdown from "@/components/Countdown.vue";
import MovingBorder from "@/components/ui/MovingBorder.vue";
import { useHead } from "@vueuse/head";

// Define page-specific metadata
useHead({
  title: "PESONA 2026 - UTDI",
  meta: [
    {
      name: "description",
      content:
        "Pesona merupakan sebuah proses seremonial penyambutan mahasiswa baru di Universitas Teknologi Digital Indonesia (UTDI).",
    },
    {
      property: "og:title",
      content: "PESONA 2026 - UTDI | Beranda",
    },
    {
      property: "og:description",
      content:
        "Pesona merupakan sebuah proses seremonial penyambutan mahasiswa baru di Universitas Teknologi Digital Indonesia (UTDI).",
    },
    {
      name: "keywords",
      content:
        "PESONA UTDI, penyambutan mahasiswa baru, UTDI, Universitas Teknologi Digital Indonesia, orientasi kampus",
    },
  ],
});
</script>

<template>
  <section
    id="beranda"
    class="relative min-h-screen flex items-center justify-center text-center text-white overflow-hidden"
  >
    <!-- Background overlay with gradient -->
    <div
      class="absolute inset-0 bg-gradient-to-br from-blue-950/95 via-blue-900/80 to-slate-900/70"
    ></div>
    <img
      src="@/assets/images/hero-bg.webp"
      alt="Hero"
      class="absolute inset-0 w-full h-full object-cover -z-10"
    />

    <!-- Main content -->
    <div
      class="container mx-auto px-4 sm:px-6 z-40 max-w-6xl relative flex flex-col items-center justify-center min-h-screen py-20 sm:py-32"
    >
      <!-- Welcome badge -->

      <!-- Main heading -->
      <h1
        class="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 leading-tight px-4"
        data-aos="fade-up"
        data-aos-delay="200"
      >
        <span
          class="block text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl mb-2"
        >
          Selamat Datang di
        </span>
        <span
          class="block bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold"
        >
          Pesona UTDI 2026
        </span>
      </h1>
      <!-- Countdown -->
      <div data-aos="fade-up" data-aos-delay="400" class="mb-2">
        <Countdown />
      </div>

      <!-- Subtitle -->
      <p
        class="text-lg sm:text-xl md:text-2xl mb-6 text-white/90 max-w-3xl mx-auto leading-relaxed font-medium px-4 text-center"
        data-aos="fade-up"
        data-aos-delay="300"
      >
        “Berakar pada budaya, bergerak dengan teknologi, bersinergi dalam kolaborasi, dan Melangkah Mendunia.”
      </p>

      <!-- CTA Button -->
      <div class="mt-4 mb-12 px-4" data-aos="fade-up" data-aos-delay="500">
        <MovingBorder
          class="transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl relative z-40 w-full sm:w-auto"
          container-class-name="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-semibold px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base"
          scroll-to="kegiatan"
        >
          <span class="inline-flex items-center justify-center">
            <span>Lihat Jadwal</span>
            <svg
              class="ml-2 w-4 h-4 sm:w-5 sm:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              ></path>
            </svg>
          </span>
        </MovingBorder>
      </div>
    </div>

    <!-- Decorative elements -->
    <div
      class="absolute top-20 left-10 w-20 h-20 bg-yellow-400/15 rounded-full blur-xl animate-pulse z-10"
    ></div>
    <div
      class="absolute top-40 right-20 w-16 h-16 bg-orange-500/15 rounded-full blur-lg animate-bounce z-10"
    ></div>
    <div
      class="absolute bottom-40 left-20 w-24 h-24 bg-red-500/15 rounded-full blur-xl animate-pulse z-10"
    ></div>

    <!-- Wave element at bottom -->
    <div class="absolute bottom-0 left-0 w-full z-20">
      <svg
        class="w-full h-24 md:h-32 fill-white transform"
        viewBox="0 0 1200 120"
        preserveAspectRatio="none"
      >
        <!-- Wave 1 -->
        <path
          d="M0,60 C300,100 600,20 900,60 C1050,90 1150,40 1200,60 L1200,120 L0,120 Z"
          class="fill-white/30 wave-1"
        ></path>
        <!-- Wave 2 -->
        <path
          d="M0,80 C250,40 550,100 850,60 C1000,20 1100,80 1200,50 L1200,120 L0,120 Z"
          class="fill-white/60 wave-2"
        ></path>
        <!-- Wave 3 -->
        <path
          d="M0,90 C200,50 500,110 800,70 C950,40 1100,90 1200,70 L1200,120 L0,120 Z"
          class="fill-white wave-3"
        ></path>
      </svg>
    </div>
  </section>
</template>
